import React from "react";
import {
  MapIcon,
  Sprout,
  Bird,
  Sun,
  BookOpen,
  Trees,
  Sparkles,
} from "lucide-react";
import { SECTIONS } from "@/constants/testIds";

const ITEMS = [
  { icon: MapIcon, label: "Country, rivers and wetlands" },
  { icon: Sprout, label: "Bush foods and medicinal plants" },
  { icon: Bird, label: "Birds and wildlife" },
  { icon: Sun, label: "Local seasons and ecological indicators" },
  { icon: BookOpen, label: "Language and cultural knowledge" },
  { icon: Trees, label: "Ranger activities and management priorities" },
  { icon: Sparkles, label: "Restoration and seed collection opportunities" },
];

const ILLUSTRATION =
  "https://images.unsplash.com/photo-1650623483248-7a2eee50f5e3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function WhatIsBushMap() {
  return (
    <section
      id="what-is"
      data-testid={SECTIONS.whatIs}
      className="relative py-24 md:py-32 bg-[#FDFBF7]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left — copy */}
          <div className="lg:col-span-6">
            <div className="bm-eyebrow mb-6">Section 02 — The Map</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2621]">
              What is a Bush Map?
            </h2>
            <div className="mt-8 space-y-5 text-[#584D45] text-lg leading-relaxed max-w-xl">
              <p className="font-serif-display italic text-2xl text-[#2C3C2B]">
                A Bush Map is more than a map.
              </p>
              <p>
                It is part visual story, part field guide, part planning tool
                and part knowledge record — a living document made with, and
                owned by, the community.
              </p>
              <p>
                Each map weaves together ecological, cultural and management
                knowledge so Country can be understood, cared for and shared
                across generations.
              </p>
            </div>

            <div className="mt-10 inline-flex items-center gap-4 px-5 py-4 border-l-2 border-[#A95A3F] bg-[#F3EFEA]">
              <span className="font-serif-display italic text-xl text-[#2C3C2B]">
                &ldquo;Country gives us everything we need.&rdquo;
              </span>
            </div>
          </div>

          {/* Right — illustrated list with image accent */}
          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-24 h-24 border border-[#C5B6A3] hidden lg:block" />
              <div className="bg-[#F3EFEA] p-8 lg:p-10 relative">
                <div className="mb-6 text-xs tracking-[0.28em] uppercase text-[#7A6C63]">
                  Each map can include
                </div>
                <ul className="space-y-4">
                  {ITEMS.map(({ icon: Icon, label }) => (
                    <li
                      key={label}
                      className="flex items-start gap-4 group"
                    >
                      <span className="mt-0.5 w-9 h-9 shrink-0 rounded-full bg-[#FDFBF7] border border-[#C5B6A3] flex items-center justify-center text-[#A95A3F] group-hover:bg-[#A95A3F] group-hover:text-white transition-colors">
                        <Icon size={16} />
                      </span>
                      <span className="text-[#2D2621] text-base md:text-lg leading-snug pt-1.5">
                        {label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Decorative photo */}
              <div className="mt-6 aspect-[16/9] overflow-hidden border border-[#E2DACF]">
                <img
                  src={ILLUSTRATION}
                  alt="Stream in Australian rainforest"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
